import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Clock, Users, ArrowRight, Loader, ShieldCheck, Globe } from 'lucide-react';
import API_URL from '../../config';
import EventProfileOnboardingModal from '../../components/events/EventProfileOnboardingModal';
import ExternalAuthModal from '../../components/events/ExternalAuthModal';
import ExternalProfileOnboardingModal from '../../components/events/ExternalProfileOnboardingModal';
import './Events.css';
import './EventsPrototype.css';

function formatDateRange(start, end) {
  if (!start) return '';
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const opts = { month: 'long', day: 'numeric', year: 'numeric' };
    if (e && s.getTime() !== e.getTime()) {
      return `${s.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}–${e.toLocaleDateString('en-US', opts)}`;
    }
    return s.toLocaleDateString('en-US', opts);
  } catch { return start; }
}

export default function ConvergenceLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasJoined, setHasJoined] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showExternalAuth, setShowExternalAuth] = useState(false);
  const [showExternalOnboarding, setShowExternalOnboarding] = useState(false);
  const [user, setUser] = useState(null);
  const [externalUser, setExternalUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Strict Light Theme enforcement for all event pages
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('vjconnect_user'));
    const storedExternal = JSON.parse(localStorage.getItem('convergence_external_user'));
    const storedExternalToken = localStorage.getItem('convergence_external_token');

    if (storedUser && storedUser.email) {
      setUser(storedUser);
      fetch(`${API_URL}/events-admin/my-events?email=${storedUser.email}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.events && data.events.some(e => e.slug === slug || storedUser.role === 'admin')) {
            setIsAdmin(true);
          } else if (storedUser.role === 'admin') {
            setIsAdmin(true);
          }
        })
        .catch(() => {});
    } else if (storedExternal && storedExternalToken) {
      // Validate active external session
      fetch(`${API_URL}/events-external-auth/session`, {
        headers: { 'Authorization': `Bearer ${storedExternalToken}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.user) {
            setUser(data.user);
            setExternalUser(data.user);
            if (data.profile_completed) {
              setHasJoined(true);
            }
          } else {
            localStorage.removeItem('convergence_external_token');
            localStorage.removeItem('convergence_external_user');
          }
        })
        .catch(() => {
          setUser(storedExternal);
          setExternalUser(storedExternal);
        });
    }
    
    // Dynamic event fetch from DB
    setLoading(true);
    fetch(`${API_URL}/events/${slug}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.event) {
          const ev = data.event;
          setEventData({
            id: ev.id,
            name: ev.name,
            slug: ev.slug,
            date: formatDateRange(ev.start_date, ev.end_date) || 'Date TBA',
            venue: ev.venue || 'VNRVJIET Campus',
            status: ev.status || 'upcoming',
            description: ev.description || 'Welcome to the networking hub for this event.',
            banner: ev.banner || ev.poster || null,
            poster: ev.poster || null,
            activities: ev.activities || [],
            speakers: ev.speakers || [],
            participant_count: ev.participant_count || 0
          });
        }
      })
      .catch(err => {
        console.error('Error fetching event data:', err);
      })
      .finally(() => setLoading(false));
    
    // Check if user has joined this event
    const activeEmail = storedUser?.email || storedExternal?.email || storedExternal?.login_email;
    if (activeEmail) {
      const locallyCompleted = localStorage.getItem(`vjlink_profile_completed_${slug}_${activeEmail}`) === 'true';
      if (locallyCompleted) {
        setHasJoined(true);
      }
      fetch(`${API_URL}/events-networking/${slug}/profile/me?email=${activeEmail}`)
        .then(res => res.json())
        .then(data => {
          if (data && (data.exists || data.profile || data.profile_completed || locallyCompleted)) {
            setHasJoined(true);
          }
        }).catch(() => {});
    }
  }, [slug]);

  // If user returns from login with intent to join, directly route to networking hub
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const shouldJoin = searchParams.get('join') === 'true' || localStorage.getItem('vjlink_pending_event_join') === slug;
    if (shouldJoin && user) {
      localStorage.removeItem('vjlink_pending_event_join');
      navigate(`/events/${slug}/networking`, { replace: true });
    }
  }, [user, slug, navigate]);

  const handleSubEventClick = (activity) => {
    if (user) {
      navigate(`/events/${slug}/networking`);
    } else {
      const joinEl = document.getElementById('join-section');
      if (joinEl) {
        joinEl.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleInternalJoin = () => {
    const targetUrl = `/events/${slug}/networking`;
    localStorage.setItem('vjlink_redirect', targetUrl);
    localStorage.setItem('vjlink_pending_event_join', slug);
    navigate(`/?redirect=${encodeURIComponent(targetUrl)}`);
  };

  const handleExternalAuthSuccess = (loggedUser, profileCompleted) => {
    setUser(loggedUser);
    setExternalUser(loggedUser);
    if (profileCompleted) {
      setHasJoined(true);
      navigate(`/events/${slug}/networking`);
    } else {
      setShowExternalOnboarding(true);
    }
  };

  const onInternalOnboardingComplete = () => {
    setShowOnboarding(false);
    setHasJoined(true);
    navigate(`/events/${slug}/networking`);
  };

  const onExternalOnboardingComplete = (updatedUser) => {
    setShowExternalOnboarding(false);
    setHasJoined(true);
    setUser(updatedUser);
    navigate(`/events/${slug}/networking`);
  };

  if (loading) {
    return (
      <div className="events-page vp-font" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <Loader size={36} className="ev-spin" style={{ color: 'var(--vp-blue)' }} />
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="events-page vp-font" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2 className="vp-heading">Event Not Found</h2>
          <p style={{ color: 'var(--vp-muted)' }}>The event "{slug}" could not be located.</p>
          <button className="vp-btn vp-btn-dark" onClick={() => navigate('/events')} style={{ marginTop: '1rem' }}>
            Back to Events Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="events-page vp-font" data-theme="light">
      <div className="events-wrapper">
        {/* ====== BACK BUTTON ====== */}
        <div style={{ marginBottom: 17 }}>
          <button className="vp-btn vp-btn-soft vp-btn-small" onClick={() => navigate('/events')}>
            ← All events
          </button>
        </div>

        {/* ====== EVENT HERO ====== */}
        <div className="vp-event-hero" data-event-name={eventData.name}>
          <div className="vp-eyebrow">
            {eventData.organization || 'VNRVJIET'} · {eventData.status === 'live' ? 'Happening now' : 'Event'}
          </div>
          <h1 className="vp-heading" style={{ fontSize: 'clamp(34px,5vw,58px)', lineHeight: 1.04, margin: '12px 0 16px' }}>
            {eventData.name}.<br />
            <span style={{ opacity: 0.7 }}>Ideas move people.</span>
          </h1>
          <p className="vp-lede">{eventData.description}</p>
          <div className="vp-event-meta-large">
            <span>◷ {eventData.date}</span>
            <span>⌖ {eventData.venue}</span>
            <span>↗ In-person</span>
            <span><Users size={14} style={{ verticalAlign: 'middle' }} /> {eventData.participant_count} registered</span>
          </div>
          <div className="vp-hero-actions" style={{ marginTop: 25 }}>
            {user ? (
              <button className="vp-btn vp-btn-dark" onClick={() => navigate(`/events/${slug}/networking`)}>
                Enter networking ↗
              </button>
            ) : (
              <button className="vp-btn vp-btn-dark" onClick={handleInternalJoin}>
                Enter networking ↗
              </button>
            )}
            {isAdmin && (
              <button className="vp-btn vp-btn-outline" onClick={() => navigate(`/events/${slug}/admin`)} style={{ borderColor: '#8c9d69', color: 'var(--vp-ink)' }}>
                ⚙️ Event Admin
              </button>
            )}
          </div>
        </div>

        {/* ====== LANDING GRID ====== */}
        <div className="vp-event-landing-grid">
          {/* Activities Panel */}
          <div className="vp-panel">
            <div className="vp-eyebrow">Find your reason to join</div>
            <h2 className="vp-heading" style={{ fontSize: 27, margin: '9px 0 8px' }}>More than a schedule.</h2>
            <p style={{ color: 'var(--vp-muted)', fontSize: 13, lineHeight: 1.7 }}>
              Explore activities, find people who share your interests, and connect with collaborators before the event gets busy.
            </p>

            {eventData.activities && eventData.activities.length > 0 ? (
              eventData.activities.map(act => (
                <div key={act.id || act.name} className="vp-activity-row" onClick={() => handleSubEventClick(act)}>
                  <div>
                    <strong>{act.name}</strong>
                    <p>{act.description || act.category || 'Activity'}</p>
                  </div>
                  <span className="vp-pill">{act.category || 'Activity'}</span>
                </div>
              ))
            ) : (
              <div style={{ padding: '1.5rem 0', color: 'var(--vp-muted)', fontSize: 13 }}>
                No sub-events announced yet. Check back soon!
              </div>
            )}
          </div>

          {/* Event Passport Sidebar */}
          <aside className="vp-panel">
            <div className="vp-eyebrow">Your event passport</div>
            <h3 className="vp-heading" style={{ fontSize: 20, margin: '8px 0 16px' }}>Make the event yours.</h3>
            
            <div className="vp-side-row">
              <div className="vp-side-icon">01</div>
              <div>
                <strong>Build your profile</strong>
                <span>Tell people what you're into.</span>
              </div>
            </div>
            <div className="vp-side-row">
              <div className="vp-side-icon">02</div>
              <div>
                <strong>Discover your people</strong>
                <span>Swipe through curated profiles.</span>
              </div>
            </div>
            <div className="vp-side-row">
              <div className="vp-side-icon">03</div>
              <div>
                <strong>Make a connection</strong>
                <span>Send a request and start a conversation.</span>
              </div>
            </div>

            {/* Join CTA */}
            <div id="join-section" style={{ marginTop: 20 }}>
              {user ? (
                <button
                  className="vp-btn vp-btn-lime"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => navigate(`/events/${slug}/networking`)}
                >
                  {hasJoined ? 'Continue networking ↗' : 'Build my network ↗'}
                </button>
              ) : (
                <>
                  <button
                    className="vp-btn vp-btn-lime"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={handleInternalJoin}
                  >
                    <ShieldCheck size={16} /> Continue with VJLINK
                  </button>
                  <div style={{ fontSize: 11, color: 'var(--vp-muted)', textAlign: 'center', margin: '6px 0 12px' }}>
                    For VNRVJIET students & faculty
                  </div>
                  <button
                    className="vp-btn vp-btn-outline"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => setShowExternalAuth(true)}
                  >
                    <Globe size={16} /> Outside VNRVJIET? Join here
                  </button>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
      
      {/* Internal Onboarding Modal */}
      {showOnboarding && (
        <EventProfileOnboardingModal 
          eventSlug={slug} 
          eventName={eventData.name} 
          user={user} 
          activities={eventData.activities} 
          onComplete={onInternalOnboardingComplete} 
          onClose={() => setShowOnboarding(false)} 
        />
      )}

      {/* External Auth Modal (Email + OTP) */}
      <ExternalAuthModal
        isOpen={showExternalAuth}
        eventSlug={slug}
        eventName={eventData?.name}
        onClose={() => setShowExternalAuth(false)}
        onSuccess={handleExternalAuthSuccess}
      />

      {/* External Profile Onboarding Modal */}
      <ExternalProfileOnboardingModal
        isOpen={showExternalOnboarding}
        eventSlug={slug}
        eventName={eventData?.name}
        user={externalUser || user}
        onComplete={onExternalOnboardingComplete}
        onClose={() => setShowExternalOnboarding(false)}
      />
    </div>
  );
}
